<!-- /src/components/app/cases/dashboard-widgets/LawSpecificDataWidget.vue -->
<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { CasesService } from '@/types/generated/services.gen'
  import {
    type CasesPartialUpdateData,
    type CustomerCase as Case,
    type GenericDataMapper,
    FieldTypeEnum
  } from '@/types/generated/types.gen'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { debounce } from '@/lib/timing'
  import { useRouter } from 'vue-router'
  import { ChevronRightIcon } from 'lucide-vue-next'

  const caseStore = useCaseStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const nonEmptyMappers = computed<GenericDataMapper[]>(() => caseStore.nonEmptyLawSpecificMappers)
  const lawSpecificMappers = computed<GenericDataMapper[]>(() => caseStore.lawSpecificMappers)

  const router = useRouter()
  // Limit the number of fields to display in the widget
  const maxVisibleFields = 5

  // Get the mappers that should be visible
  const visibleMappers = computed(() => {
    if (!nonEmptyMappers.value) return []

    // Filter mappers to only include types that should be displayed
    const displayableFieldTypes = [
      FieldTypeEnum.MULTISELECT,
      FieldTypeEnum.SELECT,
      FieldTypeEnum.TIMEFIELD,
      FieldTypeEnum.DATEFIELD,
      FieldTypeEnum.TEXTINPUT,
      FieldTypeEnum.NUMBERINPUT,
      FieldTypeEnum.DECIMALNUMBERINPUT,
      FieldTypeEnum.EMAILINPUT,
      FieldTypeEnum.PHONEINPUT,
      FieldTypeEnum.URLINPUT,
      FieldTypeEnum.YESNO
    ]

    const filteredMappers = nonEmptyMappers.value.filter((mapper) =>
      displayableFieldTypes.includes(mapper.field_type)
    )

    // Return up to maxVisibleFields mappers
    return filteredMappers.slice(0, maxVisibleFields)
  })

  // Get the value for a given mapper key
  const getFieldValue = (mapperKey: string): any => {
    if (!caseRecord.value) return null

    const [parent, field] = mapperKey.split('.')

    // Access data similarly to DatamapperTableRow
    if (caseRecord.value.data && parent && field) {
      return caseRecord.value.data[parent]?.[field]
    }

    return null
  }

  // Format display value based on type
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return ''
    }

    // Format date strings (assuming ISO format)
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const date = new Date(value)
      return date.toLocaleDateString('de-DE')
    }

    // Format date strings (already in German format)
    if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}/.test(value)) {
      return value
    }

    return value.toString()
  }

  // Copy value to clipboard
  const copyToClipboard = (value: any) => {
    if (value) {
      navigator.clipboard.writeText(value.toString())
    }
  }

  // Show copy icon when hovering over a row
  const hoveredRowIndex = ref<number | null>(null)

  const handleValueChange = debounce(async (key: string, mapperKey: string, value: any) => {
    const [parent, field] = mapperKey.split('.')
    const data = field ? { [parent]: { [field]: value } } : { [mapperKey]: value }

    const result: Case = (await CasesService.casesPartialUpdate({
      uuid: caseRecord.value?.uuid as string,
      requestBody: {
        data
      }
    } as CasesPartialUpdateData)) as Case

    // Update the case changes through the store
    if (result.data.changes) {
      caseStore.updateCaseChanges(result.data.changes)
    }
  }, 1000)

  // Navigate to full data view
  const viewAllData = () => {
    if (caseStore.caseRecord?.uuid) {
      router.push(`/app/cases/${caseStore.caseRecord.uuid}/law-specific-data`)
    }
  }
</script>

<template>
  <div class="">
    <!-- Content -->
    <div class="rounded-xl border border-t border-gray-200 bg-white shadow-sm">
      <!-- Data table when records exist -->
      <table
        v-if="visibleMappers.length > 0"
        class="w-full table-fixed"
      >
        <tbody>
          <tr
            v-for="(mapper, index) in visibleMappers"
            :key="mapper.key"
            class="border-b border-gray-200 hover:bg-gray-50"
            @mouseenter="hoveredRowIndex = index"
            @mouseleave="hoveredRowIndex = null"
          >
            <td class="border-r border-gray-200 py-3 pl-4 pr-2 text-sm font-normal text-gray-700">
              {{ mapper.label }}
            </td>
            <td class="flex items-center py-3 pl-4 pr-2">
              <div class="flex-1 text-sm font-normal text-gray-900">
                {{ formatValue(getFieldValue(mapper.key)) }}
              </div>
              <button
                class="ml-2 rounded p-1 text-gray-400 hover:text-gray-500"
                title="Kopieren"
                @click="copyToClipboard(getFieldValue(mapper.key))"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    width="14"
                    height="14"
                    x="8"
                    y="8"
                    rx="2"
                    ry="2"
                  />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty state when no records exist -->
      <div
        v-else
        class="flex flex-col items-center justify-center px-4 py-10"
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="40"
            height="40"
            rx="8"
            fill="#F3E8FF"
          />
          <path
            d="M19 20H11M24 14H11M24 26H11M27 18L23 22M23 18L27 22"
            stroke="#7E22CE"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <p class="text-md mt-5 text-center text-gray-500">Keine Rechtsbezogene Daten extrahiert</p>
      </div>
    </div>

    <!-- Footer -->
    <div class="items-left mt-4 flex justify-end">
      <a
        href="#"
        class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
        @click.prevent="viewAllData"
      >
        <span>Alle Rechtsbezogene Daten ansehen</span>
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div>
  </div>
</template>
